import * as Msal from 'msal';
import { IEmployee } from 'src/models/employee.model';
import Axios, { AxiosRequestConfig } from 'axios';

// const requiredEnv = [
//   'REACT_APP_ACTIVE_DIRECTORY_CLIENT_ID',
//   'REACT_APP_ACTIVE_DIRECTORY_AUTHORITY_URL',
//   'REACT_APP_ACTIVE_DIRECTORY_TOKEN_SCOPES',
// ];

// requiredEnv.forEach((variable) => {
//   if (!process.env[variable] || (process.env[variable] ?? '')) {
//     // eslint-disable-next-line no-alert
//     alert(
//       `Environment Variable Error: Please configure ${variable} properly in .env or .env.local file`
//     );
//   }
// });

const clientId = process.env.REACT_APP_ACTIVE_DIRECTORY_CLIENT_ID ?? '';

const msalConfig: Msal.Configuration = {
  auth: {
    clientId,
    // authority: process.env.REACT_APP_ACTIVE_DIRECTORY_AUTHORITY_URL ?? '',
    authority: `${process.env.REACT_APP_ACTIVE_DIRECTORY_AUTHORITY_URL}/${process.env.REACT_APP_ACTIVE_DIRECTORY_TENANT_ID}`,
    redirectUri:
      process.env.REACT_APP_ACTIVE_DIRECTORY_REDIRECT_URI ??
      window.location.origin,
    validateAuthority: false,
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
};

const userScopes = ['User.Read', 'profile', 'openid', 'email'];
const tokenScopes = [process.env.REACT_APP_ACTIVE_DIRECTORY_TOKEN_SCOPES ?? ''];
const msalInstance = new Msal.UserAgentApplication(msalConfig);

export const logout = () => {
  localStorage.removeItem(`${clientId}_user_data`);
  localStorage.removeItem(`${clientId}_user_token`);
  msalInstance.logout();
};

msalInstance.handleRedirectCallback((error, response) => {
  if (!error && response && response.accessToken) {
    // Checks if the callback response is related to refreshToken
    if (response.scopes.length && response.scopes[0] === tokenScopes[0]) {
      localStorage.setItem(`${clientId}_user_token`, response.accessToken);
    }
  } else if (error) {
    logout();
  }
});

// Get user details from active directory
export const getAccount = () => msalInstance.getAccount();

// Get user details from microsoft graph server
export const getUser = (): IEmployee | null => {
  const user = localStorage.getItem(`${clientId}_user_data`);

  return user ? JSON.parse(user) : null;
};

export const getToken = () => {
  return getAccount() ? localStorage.getItem(`${clientId}_user_token`) : null;
};

export const redirect = () => {
  msalInstance.loginRedirect({ scopes: tokenScopes });
};

export const refreshToken = async (
  requestScope: string[] = tokenScopes,
  returnOnly = false
) => {
  let token: string | null = null;

  if (getAccount()) {
    try {
      // Get access token
      const result = await msalInstance.acquireTokenSilent({
        scopes: requestScope,
      });

      if (result) {
        // Attach to axios global variables to be used in HTTP library
        // Or just use the getToken function of this library
        token = result.accessToken;

        if (!returnOnly) {
          localStorage.setItem(`${clientId}_user_token`, token);
        }
      }
    } catch (error) {
      // Instead of popup, we will use redirect token generator. Then, it will
      // be handled by the `msalInstance.handleRedirectCallback` function above
      // IMPORTANT!!! This feature needs the AD of this app to
      // enable `accessToken` return in the config
      if (error.name === 'InteractionRequiredAuthError') {
        msalInstance.acquireTokenRedirect({ scopes: requestScope });
      }
    }
  }

  return token;
};

// This will fetch the user details from microsoft graph server
// Please see https://developer.microsoft.com/en-us/graph/graph-explorer for the documentation
export const fetchUserData = async (): Promise<IEmployee | null> => {
  const token = await refreshToken(userScopes, true);
  const account = getAccount();

  if (token && account) {
    try {
      const config: AxiosRequestConfig = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const employeeIdResult = await Axios.get(
        `https://graph.microsoft.com/v1.0/users/${account.accountIdentifier}/employeeId`,
        config
      );

      if (employeeIdResult) {
        const { data } = await Axios.get(
          `https://graph.microsoft.com/v1.0/users/${account.accountIdentifier}`,
          config
        );

        if (data) {
          const user: IEmployee = {
            lan_user_id: data.mail.split('@')[0],
            emp_no: employeeIdResult.data.value,
            first_name: data.givenName,
            last_name: data.surname,
            e_mail_address: data.mail,
          };

          localStorage.setItem(`${clientId}_user_data`, JSON.stringify(user));

          return user;
        }
      }
    } catch (e) {
      return null;
    }
  }

  return null;
};

export const hasAuth = async () => {
  if (getToken()) {
    return true;
  }

  return !!(await refreshToken());
};

export default msalInstance;
